import { groupBy, prop } from "remeda";
import { z } from "zod";
import { isoAndUnixZodSchema, nowIsoAndUnix } from "../date-time/iso-and-unix";
import type { OrderId } from "../orders/order-id";
import { orderIdZodSchema } from "../orders/order-id";
import { productIdZodSchema } from "../products/product-id";
import { providerIdZodSchema } from "../providers/provider-id";
import type { UserId } from "../users/user-id";
import { userIdZodSchema } from "../users/user-id";
import type { Cart_ProductFormAnswer } from "./cart.product-form-answer";
import { productFormCheckboxAnswerZodObject } from "./fields/checkbox-field/product-form-field.checkbox-answer";
import type { ProductFormFieldGroupBy } from "./fields/product-form-field-quantity";
import { productFormSelectAnswerZodObject } from "./fields/select-field/product-form-field.select-answer";
import { productFormTextAnswerZodObject } from "./fields/text-field/product-form-field.text-answer";
import {
    productFormAnswerIdFactory,
    productFormAnswerIdZodSchema,
} from "./product-form-answer-id";
import { productFormIdZodSchema } from "./product-form-id";

const productFormFieldAnswerZodUnion = z.union([
    productFormCheckboxAnswerZodObject,
    productFormTextAnswerZodObject,
    productFormSelectAnswerZodObject,
]);
type ProductFormFieldAnswer = z.infer<typeof productFormFieldAnswerZodUnion>;
type ProductFormFieldAnswerType = ProductFormFieldAnswer["type"];

const productFormAnswerZodObject = z.object({
    _id: productFormAnswerIdZodSchema,
    _type: z.literal("PRODUCT_FORM_ANSWER"),
    _createdAt: isoAndUnixZodSchema,
    productFormId: productFormIdZodSchema,
    orderId: orderIdZodSchema,
    productId: productIdZodSchema,
    providerId: providerIdZodSchema,
    userId: userIdZodSchema,
    answers: z.array(productFormFieldAnswerZodUnion),
});

type ProductFormAnswer = z.infer<typeof productFormAnswerZodObject>;

const factory = (params: {
    answerFromCart: Cart_ProductFormAnswer;
    orderId: OrderId;
    userId: UserId;
}): ProductFormAnswer => {
    const { orderId, userId } = params;
    const { _id, _type, _createdAt, ...rest } = params.answerFromCart;
    return {
        ...rest,
        _id: productFormAnswerIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _type: "PRODUCT_FORM_ANSWER",
        orderId,
        userId,
    };
};

const groupAnswers = (
    answers: ProductFormFieldAnswer[],
): {
    sortOrder: ProductFormFieldGroupBy[];
    groupedAnswers: Record<ProductFormFieldGroupBy, ProductFormFieldAnswer[]>;
} => {
    const groupSortOrder: ProductFormFieldGroupBy[] = [];
    answers.forEach(answer => {
        if (answer.groupedBy && !groupSortOrder.includes(answer.groupedBy)) {
            groupSortOrder.push(answer.groupedBy);
        }
    });
    const groupedAnswers = groupBy(answers, prop("groupedBy"));
    return {
        groupedAnswers,
        sortOrder: groupSortOrder,
    };
};

const productFormAnswerUtils = {
    groupAnswers,
};

export {
    factory as productFormAnswerFactory,
    productFormAnswerUtils,
    productFormAnswerZodObject,
    productFormFieldAnswerZodUnion,
};
export type {
    ProductFormAnswer,
    ProductFormFieldAnswer,
    ProductFormFieldAnswerType,
};
